import { CalendarDays, Clock3, Armchair, Trophy } from 'lucide-react';
import { PlayerPage, LeaguePicker } from './PlayerPage';
import { LINK_CLASS } from '@league/ui';
import { standingLeaders } from './standing-leaders';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useState } from 'react';

import type {
  PlayerStandingRow,
  PlayerStandings,
  PlayerTodayCard,
  PlayerTodayResponse,
} from '@league/shared/contract';

import { Button, ErrorNote } from '../../components/ui';
import { ScoreCard } from './ScoreCard';
import { ApiError, apiDelete, apiFetch, apiPost } from '../../lib/api';
import { leagueDate, leagueTime } from '../../lib/when';

/**
 * TODAY — the screen the whole player app exists for.
 *
 * A player opens this standing in a doorway, one-handed, five minutes before play, wanting one
 * thing: where do I sit and who am I sitting with. Everything else on the page is below that.
 *
 * THE STATE THAT MATTERS is the one where there is no answer yet. Seating is arranged days ahead
 * and published when the director is happy with it, so between those two moments the honest reply
 * is "not yet" — not an empty grid, and never a guess. The API will not send a table before it is
 * published, so this screen cannot show one by mistake.
 *
 * WHAT THIS SCREEN DOES NOT DECIDE (2026-09-26). It used to count days itself, in the phone's zone,
 * and clamp the result at zero — so a league that finished weeks ago read "IN 0 DAYS" with an open
 * score card. Whether she is playing, waitlisted or subbing, whether the league is over, and how
 * many days away the session is in the LEAGUE's city all arrive from the API now; this renders them.
 */

export function TodayPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const leagueId = params.get('leagueId');
  const setLeagueId = (id: string) => {
    setConfirming(false);
    setParams({ leagueId: id });
  };
  const [confirming, setConfirming] = useState(false);

  const today = useQuery({
    queryKey: ['my-today', leagueId],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    queryFn: () =>
      apiFetch<PlayerTodayResponse>(
        `/api/my/today${leagueId ? `?leagueId=${encodeURIComponent(leagueId)}` : ''}`,
      ),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['my-today'] });

  const away = useMutation({
    mutationFn: (sessionId: string) =>
      apiPost<{ absence: unknown }>(`/api/my/sessions/${sessionId}/absence`, {}),
    onSuccess: async () => {
      setConfirming(false);
      await refresh();
    },
  });

  const back = useMutation({
    mutationFn: (sessionId: string) => apiDelete(`/api/my/sessions/${sessionId}/absence`),
    onSuccess: refresh,
  });

  if (today.isPending) {
    return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;
  }

  const data = today.data;

  if (today.isError) {
    return (
      <Shell>
        <ErrorNote>
          Could not load your league.{' '}
          <Button variant="secondary" onClick={() => void today.refetch()}>
            Try again
          </Button>
        </ErrorNote>
      </Shell>
    );
  }

  if (!data?.profile) {
    return (
      <Shell>
        <p className="text-[15px] leading-relaxed text-ink-2">
          This account is not linked to a player record yet. Your director can sort that out.
        </p>
      </Shell>
    );
  }

  if (!data.today) {
    return (
      <Shell>
        <header className="player-page-header">
          <p className="player-eyebrow">My League</p>
          <h1>Hello, {data.profile.firstName}</h1>
        </header>
        <p className="player-card player-empty">
          {data.leagues.length
            ? 'No session is available for this selection. Choose one of your leagues below or ask your Director.'
            : 'You are not in a league at the moment. When you join one, this is where your table will be.'}
        </p>
        {data.leagues.length ? (
          <LeaguePicker leagues={data.leagues} value={leagueId} onChange={setLeagueId} />
        ) : null}
      </Shell>
    );
  }

  const session = data.today;
  const over = session.leagueFinished || session.daysAway < 0;
  const mayReportAway = session.role === 'playing' && session.seating !== 'away' && !over;

  return (
    <Shell>
      <header className="player-page-header">
        <p className="player-eyebrow">My League</p>
        <h1>{session.leagueName}</h1>
        <p className="player-page-intro">
          {session.leagueFinished
            ? 'Your league, from the first table to the final results.'
            : 'Your next session, your table, and everything you need to play.'}
        </p>
      </header>
      {/* A player in two leagues at once. Absent for everybody else, which is almost everybody. */}
      {data.leagues.length > 1 ? (
        <LeaguePicker leagues={data.leagues} value={session.leagueId} onChange={setLeagueId} />
      ) : null}

      {away.error ? <ErrorNote>Could not record that. Try again.</ErrorNote> : null}
      {back.error ? (
        <ErrorNote>
          {back.error instanceof ApiError
            ? back.error.message
            : 'Could not change that. Try again.'}
        </ErrorNote>
      ) : null}

      <div
        className={`player-league-grid${session.role !== 'substitute' && data.approvedStandings?.rows.some((row) => row.isYou) ? ' has-standings' : ''}`}
      >
        <div className="player-session-column">
          <section className="player-card player-session">
            <div className="player-session-meta">
              <When session={session} />
              <span className="text-[13px] text-ink-3">
                {over ? `${session.sessionsTotal} sessions` : `Session ${session.sessionNumber}`}
              </span>
            </div>

            <h2>
              {session.leagueFinished
                ? 'A league to remember'
                : session.seating === 'published' && !over
                  ? 'At the table'
                  : 'Your session'}
            </h2>

            {over ? null : (
              <div className="player-session-facts">
                <p>
                  <CalendarDays aria-hidden="true" />
                  <span>
                    {leagueDate(session.startsAt, session.timeZone, {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                    {session.venueName ? ` · ${session.venueName}` : ''}
                  </span>
                </p>
                {/*
              THE START TIME, at last. It was a bracket — "ASK YOUR DIRECTOR" — because there was no
              zone to read it in, and a guessed time on the screen somebody uses to decide when to
              leave the house is the worst place for one. The market has a zone now; the time is read
              in it and says which, so nobody has to guess.
            */}
                <p>
                  <Clock3 aria-hidden="true" />
                  <span>Starts {leagueTime(session.startsAt, session.timeZone)}</span>
                </p>
              </div>
            )}

            {session.role === 'substitute' && !over ? (
              <p className="mt-3 text-[14px] text-ink-2">
                You are filling in as a substitute for this session.
              </p>
            ) : null}

            <div className="mt-6">
              <Body
                session={session}
                standings={data.standings}
                withdrawing={back.isPending}
                onWithdraw={() => back.mutate(session.sessionId)}
              />
            </div>

            {mayReportAway ? (
              <div className="mt-6 border-t border-light pt-5">
                {confirming ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      disabled={away.isPending}
                      onClick={() => away.mutate(session.sessionId)}
                    >
                      {away.isPending ? 'Recording…' : 'Yes, I cannot make it'}
                    </Button>
                    <Button variant="secondary" onClick={() => setConfirming(false)}>
                      Never mind
                    </Button>
                  </div>
                ) : (
                  <Button variant="secondary" onClick={() => setConfirming(true)}>
                    Can&rsquo;t make it?
                  </Button>
                )}
              </div>
            ) : null}
          </section>
          {/*
          HER TABLE'S CARD, and only when there is one. `ScoreCard` renders nothing at all unless the
          API hands it a card, so the rule about when it appears lives in one place — the query —
          rather than being kept here as well and drifting. Existing scores remain visible when locked.
        */}
          {!session.leagueFinished ? (
            <ScoreCard
              key={session.sessionId}
              sessionId={session.sessionId}
              playerId={data.profile.playerId}
            />
          ) : null}
        </div>

        {session.role !== 'substitute' && data.approvedStandings?.rows.some((row) => row.isYou) ? (
          <Snippet standings={data.approvedStandings.rows} final={data.approvedStandings.final} />
        ) : null}
      </div>
    </Shell>
  );
}

/** The chip above the league's name: when this is, in the league's own calendar. */
function When({ session }: { session: PlayerTodayCard }) {
  const label = session.leagueFinished
    ? 'Finished'
    : session.daysAway < 0
      ? 'All sessions played'
      : session.daysAway === 0
        ? 'Today'
        : session.daysAway === 1
          ? 'Tomorrow'
          : `In ${session.daysAway} days`;

  /* The one signal element at rest on this screen, and only on the day itself. */
  const isToday = !session.leagueFinished && session.daysAway === 0;

  return (
    <span
      className={[
        'rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em]',
        isToday ? 'bg-signal text-on-signal' : 'bg-sand text-ink-2',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

/**
 * What goes in the middle of the card. One answer per state, and each is a sentence she can act on
 * rather than an empty grid.
 */
function Body({
  session,
  standings,
  withdrawing,
  onWithdraw,
}: {
  session: PlayerTodayCard;
  standings: PlayerStandings;
  withdrawing: boolean;
  onWithdraw: () => void;
}) {
  if (session.leagueFinished) {
    const me = standings.rows.find((row) => row.isYou);
    return (
      <Panel>
        <p className="text-[15px] font-medium">This league has finished.</p>
        <p className="mt-1 text-[14px] text-ink-2">
          {me && session.role !== 'substitute'
            ? `You finished ${ordinal(me.rank)}${me.tied ? ', level with somebody else' : ''} of ${standings.ranked}. `
            : ''}
          Thank you for playing.
        </p>
        {session.role !== 'substitute' ? (
          <Link
            to={`/my/standings?leagueId=${encodeURIComponent(session.leagueId)}`}
            className={`mt-3 inline-block min-h-[44px] py-2 text-[14px] ${LINK_CLASS}`}
          >
            See the final standings
          </Link>
        ) : null}
      </Panel>
    );
  }

  if (session.role === 'waitlist') {
    return (
      <Panel>
        <p className="text-[15px] font-medium">
          You are on the waitlist
          {session.waitlistPosition ? ` — number ${session.waitlistPosition}` : ''}.
        </p>
        <p className="mt-1 text-[14px] text-ink-2">
          If a seat opens, your director will move you in. Your table will show here once you have a
          seat.
        </p>
      </Panel>
    );
  }

  if (session.role === 'registered') {
    return (
      <Panel>
        <p className="text-[15px] font-medium">Your seat is held.</p>
        <p className="mt-1 text-[14px] text-ink-2">
          Your director confirms it once your payment is through. After that, your table shows here
          once seating is finalized.
        </p>
      </Panel>
    );
  }

  if (session.daysAway < 0 && session.seating !== 'published') {
    return (
      <Panel>
        <p className="text-[15px] font-medium">That was the last session.</p>
        <p className="mt-1 text-[14px] text-ink-2">
          The standings become final once your director closes the league.
        </p>
      </Panel>
    );
  }

  if (session.seating === 'away') {
    return (
      <Panel>
        <p className="text-[15px] font-medium">
          Your absence is recorded for this session and visible to your Director in the app.
        </p>
        {session.absenceWithdrawable ? (
          <>
            <p className="mt-1 text-[14px] text-ink-2">
              Changed your mind? You can take it back before the session starts, while seating is
              not finalized and no substitute is assigned.
            </p>
            <Button
              variant="secondary"
              className="mt-3"
              disabled={withdrawing}
              onClick={onWithdraw}
            >
              {withdrawing ? 'Telling your director…' : 'I can make it after all'}
            </Button>
          </>
        ) : (
          <p className="mt-1 text-[14px] text-ink-2">
            Contact your Director about changes or substitute arrangements.
          </p>
        )}
      </Panel>
    );
  }

  if (session.seating === 'not_published') {
    return (
      <>
        {/*
          NOT AN EMPTY GRID. The arrangement exists in draft days before it is published and it moves
          while the director works on it; showing a player a table that is still moving is how
          somebody ends up sitting in the wrong chair.
        */}
        <Panel>
          <p className="text-[15px] font-medium">
            Your Director is getting the tables ready. Your table will appear here once seating is
            finalized.
          </p>
        </Panel>
        {session.playing.length > 0 ? <Playing names={session.playing} /> : null}
      </>
    );
  }

  return <Table session={session} />;
}

/**
 * WHO IS PLAYING, before the tables go up. Ruled 2026-09-25: first names and last initials, no
 * contact details, and nothing that reads as a table assignment.
 *
 * So it is a sentence, not a grid. Names in a flowing line, alphabetical, with a count — no boxes, no
 * rows of four, nothing a person could read as "these are my seatmates".
 */
function Playing({ names }: { names: string[] }) {
  return (
    <section className="mt-5">
      <h2 className="text-[11px] uppercase tracking-[0.12em] text-ink-3">
        Playing in this league <span className="tabular">({names.length})</span>
      </h2>
      {/* A name never breaks across lines — "Margaret" at the end of one and "Q." on the next. */}
      <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
        {names.map((name) => name.replace(/ /g, ' ')).join(', ')}
      </p>
    </section>
  );
}

/**
 * The table, and the line underneath it that no spreadsheet could write.
 *
 * "You asked to sit with Ruth — done" and "you haven't played Marcy yet" are the two facts only
 * this platform holds, and they are the reason somebody opens the app rather than texting a friend.
 */
function Table({ session }: { session: PlayerTodayCard }) {
  const others = session.seatmates.filter((mate) => !mate.isYou);
  const asked = others.filter((mate) => mate.askedFor).map((mate) => mate.firstName || mate.name);
  /* "Not played yet this league" means nothing to a substitute, who is not in the league. */
  const fresh =
    session.role === 'substitute'
      ? []
      : others.filter((mate) => !mate.playedBefore).map((mate) => mate.firstName || mate.name);

  return (
    <div>
      <div className="player-table-number">
        <Armchair aria-hidden="true" />
        <div>
          <p className="text-[11px] uppercase tracking-[0.12em] text-ink-3">Your table</p>
          {/*
            INK, NOT RED. A table number is information, not urgency — and it is already 40px in the
            display face, which is how you find it. This screen had TEN red things on it at rest;
            the accent stops meaning anything at that density, and the one element that should keep
            it is the row that says which of these four people is you.
          */}
          <p className="player-table-value tabular">{session.tableNumber}</p>
        </div>
        {/*
          ⚠ NO SEAT, ON PURPOSE — owner, 2026-09-26. East, South, West and North were shown here and
          beside each seatmate. A player sits down wherever at her table and the winds are settled
          there; what she needs from her phone is the table number and who she is playing. The seat
          is still stored and still on the director's board and score sheet, where it is used.
        */}
      </div>

      <p className="mt-6 text-[11px] uppercase tracking-[0.12em] text-ink-3">Who you are playing</p>
      <ul className="player-seatmates mt-2">
        {session.seatmates.map((mate) => (
          <li
            key={mate.playerId}
            className={[
              'rounded-md px-3.5 py-2.5',
              mate.isYou ? 'bg-tint-signal text-on-tint-signal' : 'bg-sand',
            ].join(' ')}
          >
            {/*
              Her own row keeps the tint and its one permitted text colour — that is the single
              accent this screen spends, and it answers the only question the screen exists to
              answer: which of these four is me.
            */}
            <span className="text-[15px] font-medium">{mate.isYou ? 'You' : mate.name}</span>
          </li>
        ))}
      </ul>

      {asked.length > 0 || fresh.length > 0 ? (
        <p className="mt-4 text-[14px] leading-relaxed text-ink-2">
          {asked.length > 0 ? <>You asked to sit with {list(asked)} — done. </> : null}
          {fresh.length > 0 ? (
            <>You have not been seated with {list(fresh)} in earlier sessions of this league.</>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The leaders and her. ⚠ Owner, 2026-09-26: the top five and her own place — never the rows around
 * her, which is where somebody on no points would be. The API sends only those rows.
 */
export function Snippet({ standings, final }: { standings: PlayerStandingRow[]; final: boolean }) {
  const leaders = standingLeaders(standings);
  const me = standings.find((row) => row.isYou);
  const own = me && !leaders.some((row) => row.playerId === me.playerId) ? me : null;
  const row = (person: PlayerStandingRow, separate = false) => (
    <li
      key={person.playerId}
      className={`flex items-baseline gap-3 px-4 py-3 ${person.isYou ? 'bg-tint-signal text-on-tint-signal' : ''}`}
    >
      {person.sessionsPlayed > 0 ? (
        <span className={`tabular w-6 shrink-0 text-[13px] ${person.isYou ? '' : 'text-ink-3'}`}>
          {person.rank}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 break-words text-[15px]">
        {separate ? 'Your standing' : person.isYou ? 'You' : person.name}
      </span>
      {person.sessionsPlayed > 0 ? (
        <span className="tabular text-[15px] font-medium">{person.points}</span>
      ) : (
        <span className="text-[13px]">No approved results yet</span>
      )}
    </li>
  );
  return (
    <section aria-label="Your league standing" className="player-card player-standing-snippet">
      <div className="player-section-title">
        <Trophy aria-hidden="true" />
        <h2>{final ? 'Where you finished' : 'Where you stand'}</h2>
      </div>
      {leaders.length ? (
        <ul className="mt-2 flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
          {leaders.map((person) => row(person))}
        </ul>
      ) : (
        <p className="mt-2 text-[14px] text-ink-2">No leaders yet.</p>
      )}
      {own ? (
        <ul className="mt-2 overflow-hidden rounded-md border border-light">{row(own, true)}</ul>
      ) : null}
    </section>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <PlayerPage className="player-league-page">{children}</PlayerPage>;
}

function Panel({ children }: { children: React.ReactNode }) {
  return <div className="player-state-panel">{children}</div>;
}

/*
  ⚠ A THIRD `Bracket` LIVED HERE, and it still carried a bug the other two had already had fixed.
  Its dashed border was `stroke`, which is 1.64:1 on paper — a row separator, not a carrier of
  meaning — so on the one screen a player opens standing in a doorway, the thing marking a fact as
  missing was the thing hardest to see. `@league/ui`'s version uses `control-border` at 5.12:1 and
  is ground-aware.

  Deleted 2026-09-12 with the site's own copy, so there is one placeholder style in the product
  rather than three. The last bracket on this screen — the start time — went on 2026-09-26.
*/

/** "Ruth", "Ruth and Joan", "Ruth, Joan and Marcy" — written out, not comma-separated. */
function list(names: string[]): string {
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** 1st, 2nd, 3rd, 4th … 11th, 12th, 13th … 21st. */
function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'}`;
}
